import { defineComponent as _defineComponent } from 'vue'
import { makeFormModel, getChildModel, setState } from 'ah-common-lib/src/form/helpers';
import { checkboxField } from 'ah-common-lib/src/form/models';
import ClientDocumentsUploader from '@/app/components/settings/client/ClientDocumentsUploader.vue';
import TermsAcceptanceForm from './TermsAcceptanceForm.vue';
import {
  ClientType,
  ClientFileCategories,
  UploadedFile,
  CompanyRegistrationModel,
  UnsubmittedUbo,
  ComplianceErrorCodes,
  CurrentAddressHistoryItem,
  AddressHistoryItem,
  EntityTypeAddress,
  getAddressHistoryError,
  checkAddressHistoryValidity,
  BaseIndividual,
} from 'ah-api-gateways';
import UBOForm from '../company/UBOForm.vue';
import { FormDefinition, FormValidation } from 'ah-common-lib/src/form/interfaces';
import { useAuthStore } from '@/app/store/authStore';
import { computed, reactive, ref, watch } from 'vue';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { getServices } from '@/app/services';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { cloneDeep } from 'lodash';
import { catchError, defaultIfEmpty, mergeMap, tap } from 'rxjs/operators';
import { HttpError } from 'ah-requests/http/httpService';
import { useToast } from 'ah-common-lib/src/toast';
import { isTempUUID } from 'ah-common-lib/src/helpers/uuid';
import { useFEFeatureFlag } from 'ah-common-stores';


export default /*#__PURE__*/_defineComponent({
  __name: 'RegistrationDocumentsForm',
  props: {
    model: null
  },
  emits: ["update:validation", "proceed"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:validation', value: FormValidation<CompanyRegistrationModel>): void;
  (e: 'proceed', value: void): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { model: Partial<CompanyRegistrationModel> };



const requestManager = useRequestManager().manager;

const services = getServices();

const authStore = useAuthStore();

const toast = useToast();

const showUboAddressHistoryErrors = ref(false);

const state = reactive<{
  uboUsers: UnsubmittedUbo[];
  files: UploadedFile[];
  uboValidation: FormValidation | null;
  termsValidation: FormValidation | null;
}>({
  uboUsers: [],
  files: [],
  uboValidation: null,
  termsValidation: null,
});



const uploadLaterFM = reactive<FormDefinition>({
  form: makeFormModel({
    name: 'uploadLater',
    fieldType: 'form',
    fields: [
      checkboxField('laterUpload', 'I will upload missing documents later.', false, {
        required: false,
        errorMessages: {
          mustAccept: 'Must confirm this or upload your identification documents to continue',
        },
      }),
    ],
  }),
  validation: null,
});

function loadDocuments() {
  requestManager
    .sameOrCancelAndNew(
      'getClientDocuments',
      services.client.getDocuments(authStore.loggedInIdentity!.client!.id, { errors: { silent: true } })
    )
    .subscribe((response) => (state.files = response));
}

loadDocuments();

const isOwner = computed(() => {
  if (authStore.loggedInIdentity?.client?.type !== ClientType.COMPANY) {
    return true;
  }
  if (props.model.individuals) {
    const owner = props.model.individuals.find((i) => i.owner);
    if (owner) {
      return owner.id === authStore.loggedInIdentity.id;
    }
  }
  return !!authStore.loggedInIdentity?.owner;
});

const owner = computed(() => {
  return props.model.individuals?.find((i) => i.owner);
});

const secondaryOwner = computed(() => {
  return props.model.individuals?.find((i) => i.secondaryOwner);
});

const isCompany = computed(() => authStore.isCompanyClient);

const termsAndConditionsRequired = computed(() => !isCompany.value || isOwner.value);

const uploadingLater = computed(() => uploadLaterFM.form.laterUpload);

const hideOnboardingDocumentsUploader = useFEFeatureFlag('hideOnboardingDocumentsUploader');

const fileCategories = computed(() => {
  if (isCompany.value) {
    const out = [
      ClientFileCategories.PROOF_OF_ADDRESS,
      ClientFileCategories.PHOTO_ID,
      ClientFileCategories.INCORPORATED_DOCUMENT,
      ClientFileCategories.FINANCIAL_STATEMENT,
      ClientFileCategories.SAMPLE_INVOICE,
    ];
    if (!state.uboUsers?.length) {
      out.push(ClientFileCategories.RESOLUTION_LETTER);
    }
    if (!!props.model.individuals?.find((i) => i.secondaryOwner)) {
      out.push(ClientFileCategories.SECONDARY_OWNER_PROOF_OF_ADDRESS, ClientFileCategories.SECONDARY_OWNER_PHOTO_ID);
    }
    return out;
  } else {
    if (!hideOnboardingDocumentsUploader.value) {
      return [ClientFileCategories.PROOF_OF_FUNDS];
    } else {
      return [
        ClientFileCategories.PROOF_OF_ADDRESS,
        ClientFileCategories.PHOTO_ID,
        ClientFileCategories.PROOF_OF_FUNDS,
      ];
    }
  }
});

const allFilesUploaded = computed(() => {
  if (filteredFiles.value.length < fileCategories.value.length) {
    return false;
  }

  if (isCompany.value) {
    for (let i = 0; i < (state.uboUsers?.length ?? 0); i++) {
      const user = state.uboUsers![i];
      if (!user.readyToUpload || user.readyToUpload.filter((i) => !!i.file).length < 2) {
        return false;
      }
    }
  }
  return true;
});

const filteredFiles = computed(() => {
  return state.files.filter((file) => fileCategories.value.includes(file.category as ClientFileCategories));
});

const proofOfFundsUploaded = computed(() =>
  state.files.some((file) => file.category === ClientFileCategories.PROOF_OF_FUNDS)
);

const validUploadedFiles = computed(() => {
  if (isCompany.value) {
    return uploadingLater.value || allFilesUploaded.value || hideOnboardingDocumentsUploader.value;
  } else {
    return proofOfFundsUploaded.value || uploadingLater.value;
  }
});

const areFormsInvalid = computed(() => {
  if (!isOwner.value) {
    return !validUploadedFiles.value;
  } else {
    return !validUploadedFiles.value || (termsAndConditionsRequired.value && state.termsValidation?.$invalid);
  }
});

const validation = computed(() => {
  return {
    $model: {},
    $invalid: areFormsInvalid.value || state.uboValidation?.$invalid,
    $dirty:
      state.uboValidation?.$anyDirty ||
      !!state.termsValidation?.$dirty ||
      state.files.length > 0 ||
      uploadingLater.value,
  } as FormValidation<CompanyRegistrationModel>;
});

const clientId = computed(() => authStore.loggedInIdentity!.client!.id);

const showUploadLaterCheckbox = computed(() => {
  if (isCompany.value) {
    return !hideOnboardingDocumentsUploader.value && !allFilesUploaded.value;
  } else {
    return !proofOfFundsUploaded.value && (hideOnboardingDocumentsUploader.value || !allFilesUploaded.value);
  }
});

function approveTermsRequest() {
  if (termsAndConditionsRequired.value) {
    // FIXME we skip any errors related to "Terms and conditions already accepted" as it is a known API issue
    return services.client
      .acceptTermsAndConditions(clientId.value, {
        options: {
          errors: {
            silent(error) {
              return error.response?.data.code === ComplianceErrorCodes.TERMS_AND_CONDITIONS_ALREADY_APPROVED;
            },
          },
        },
      })
      .pipe(
        catchError((error: HttpError) => {
          if (error.response?.data.code === ComplianceErrorCodes.TERMS_AND_CONDITIONS_ALREADY_APPROVED) {
            return of(null);
          }
          return throwError(error);
        })
      );
  } else {
    return of(null);
  }
}

function skipDocumentsRequest() {
  const shouldSkip =
    hideOnboardingDocumentsUploader.value ||
    uploadingLater.value ||
    (!isCompany.value && filteredFiles.value.length === 0);

  return shouldSkip ? services.compliance.uploadLater(clientId.value) : of(undefined);
}

function checkUboAddressHistoryValidity() {
  const anyInvalid = state.uboUsers.some((ubo) => {
    if (ubo.currentAddress.countryCode !== 'GB') {
      return false;
    }
    const validityCheck = checkAddressHistoryValidity(ubo);
    return !validityCheck.age || !validityCheck.gaps;
  });
  return !anyInvalid;
}

function uboSubmissionRequest() {
  const requests: Observable<any>[] = [];

  props.model.uboUsers?.forEach((savedUbo) => {
    if (savedUbo.id && !isTempUUID(savedUbo.id) && !state.uboUsers.some((ubo) => ubo.id === savedUbo.id)) {
      requests.push(services.compliance.removeUboUser(clientId.value, savedUbo.id));
    }
  });

  state.uboUsers?.forEach((ubo) => {
    const { documents, readyToUpload, ...uboInfo } = cloneDeep(ubo);
    if (uboInfo.phoneNumber?.length === 0) delete uboInfo.phoneNumber;

    const request =
      uboInfo.id && !isTempUUID(uboInfo.id)
        ? services.compliance.updateUboUser(clientId.value, uboInfo.id, uboInfo as BaseIndividual)
        : services.compliance.createUboUser(clientId.value, uboInfo as BaseIndividual);

    requests.push(
      request.pipe(
        tap((response) => {
          Object.assign(ubo, response);
        }),
        mergeMap((response) =>
          forkJoin([
            ...(readyToUpload ?? [])
              .filter((file) => !!file.file)
              .map((file) =>
                services.compliance.submitUboDocument(clientId.value, response.id!, file.category, file.file!)
              ),
            services.compliance.createAddressHistory({
              currentAddress: ubo.currentAddress,
              previousAddresses: ubo.previousAddresses,
              entityId: response.id!,
              type: EntityTypeAddress.UBO,
            }),
          ]).pipe(defaultIfEmpty([] as any[]))
        ),
        catchError((error: HttpError) => {
          const errorMessage = getAddressHistoryError(error);

          if (errorMessage) {
            toast.error(errorMessage);
          }

          return throwError(error);
        })
      )
    );
  });

  return requests;
}

function submit() {
  const validAddressHistories = checkUboAddressHistoryValidity();
  if (!validAddressHistories) {
    toast.error(
      'Please review UBO addresses. UK residents must provide addresses spanning back 3 years, with no gaps.'
    );
    showUboAddressHistoryErrors.value = true;
    return;
  }

  requestManager
    .currentOrNew(
      'submitRegistration',
      forkJoin(uboSubmissionRequest()).pipe(
        defaultIfEmpty([null]),
        mergeMap(() => skipDocumentsRequest()),
        mergeMap(() => approveTermsRequest())
      )
    )
    .subscribe(() => emit('proceed'));
}

watch(
  () => props.model,
  () => {
    if (isCompany.value) {
      if (props.model.uboUsers?.length) {
        state.uboUsers = cloneDeep(props.model.uboUsers);
      } else {
        const ubos: UnsubmittedUbo[] = [];
        if (props.model.individuals) {
          [props.model.individuals.find((i) => i.owner), props.model.individuals.find((i) => i.secondaryOwner)].forEach(
            (individual) => {
              if (individual) {
                ubos.push({
                  firstName: individual.firstName,
                  lastName: individual.lastName,
                  title: individual.title,
                  email: individual.email,
                  phoneNumber: individual.phoneNumber,
                  birthDate: individual.birthDate,
                  documents: [],
                  currentAddress: individual.currentAddress as CurrentAddressHistoryItem,
                  previousAddresses: individual.previousAddresses as AddressHistoryItem[],
                  ownershipPercentage: 25.0,
                });
              }
            }
          );
        }
        state.uboUsers = ubos;
      }
    }
  },
  { immediate: true }
);

watch(
  allFilesUploaded,
  () => {
    setState(getChildModel(uploadLaterFM.form, 'laterUpload')!, 'hidden', allFilesUploaded.value);
  },
  { immediate: true, deep: true }
);

return { __sfc: true,props, requestManager, services, authStore, toast, showUboAddressHistoryErrors, state, emit, uploadLaterFM, loadDocuments, isOwner, owner, secondaryOwner, isCompany, termsAndConditionsRequired, uploadingLater, hideOnboardingDocumentsUploader, fileCategories, allFilesUploaded, filteredFiles, proofOfFundsUploaded, validUploadedFiles, areFormsInvalid, validation, clientId, showUploadLaterCheckbox, approveTermsRequest, skipDocumentsRequest, checkUboAddressHistoryValidity, uboSubmissionRequest, submit, ClientDocumentsUploader, TermsAcceptanceForm, ClientFileCategories, UBOForm }
}

})